import { TypedDocumentNode, gql } from '@apollo/client';
import classNames from 'classnames';
import { CSSProperties } from 'react';
import styled from 'styled-components';

import { FootballRivalsOutcome } from '@sorare/core/src/__generated__/globalTypes';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Box } from '@sorare/core/src/atoms/ui/Box';

import { GridBackground } from '@sorare/gameplay/src/components/rivals/GridBackground';

import { Lineup } from 'components/rivals/Lineup';
import { ManagerPoints } from 'components/rivals/ManagerPoints';
import { useOutcomeConfig } from 'components/rivals/useOutcomeConfig';

import { DoubleAnimatedDiamond } from './DoubleAnimatedDiamond';
import { Scores } from './Scores';
import { TacticPreview } from './TacticPreview';
import { ArenaStoryElement_footballRivalsChallenge } from './__generated__/index.graphql';

const Root = styled(Vertical).attrs({ center: true, gap: 4 })`
  width: 100%;
  height: 100%;
  position: relative;
  isolation: isolate;
  padding: calc(9 * var(--unit)) var(--double-unit) var(--double-unit);
`;
const GridBackgroundWrapper = styled.div`
  position: absolute;
  height: 100%;
  z-index: -1;
  inset: 0;
  overflow: hidden;
`;
const Gradient = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    to bottom,
    rgba(var(--color), 0.4) 0%,
    transparent 100%
  );
`;
const LineupWrapper = styled(Box)`
  background: rgba(var(--c-rgb-neutral-100), 0.54);
  backdrop-filter: blur(36px);

  &.winner {
    background:
      linear-gradient(rgba(var(--color), 0.12), rgba(var(--color), 0.12))
        padding-box,
      linear-gradient(var(--c-neutral-100), var(--c-neutral-100)) padding-box,
      linear-gradient(
          to bottom,
          var(--c-rivals-friendlies) 0%,
          var(--c-rivals-win) 100%
        )
        border-box;
    border: 2px solid transparent;
  }
`;

type Props = { challenge: ArenaStoryElement_footballRivalsChallenge };
export const ArenaStoryElement = ({ challenge }: Props) => {
  const { homeContestant, awayContestant } = challenge;

  const { color, hexColor, text } = useOutcomeConfig(homeContestant?.outcome);

  if (!homeContestant?.outcome || !awayContestant) {
    return null;
  }

  const homeContestantWinner =
    homeContestant.outcome !== FootballRivalsOutcome.LOSE;

  return (
    <Root style={{ '--color': color } as CSSProperties}>
      <GridBackgroundWrapper>
        <GridBackground height="100%" color={hexColor} />
        <Gradient />
      </GridBackgroundWrapper>
      <LineupWrapper className={classNames({ winner: homeContestantWinner })}>
        <Vertical gap={2}>
          <ManagerPoints
            lineup={homeContestant.lineup}
            opponentScore={awayContestant.lineup?.score}
          />
          <Lineup
            lineup={homeContestant.lineup}
            rarityBonusEnabled={challenge.rarityBonusEnabled}
            compact
          />
          <TacticPreview lineup={homeContestant.lineup} />
        </Vertical>
      </LineupWrapper>
      <Scores homeContestant={homeContestant} awayContestant={awayContestant} />
      <LineupWrapper className={classNames({ winner: !homeContestantWinner })}>
        <Vertical gap={2}>
          <ManagerPoints
            lineup={awayContestant.lineup}
            opponentScore={homeContestant.lineup?.score}
          />
          <Lineup
            lineup={awayContestant.lineup}
            rarityBonusEnabled={challenge.rarityBonusEnabled}
            compact
          />
          <TacticPreview lineup={awayContestant.lineup} />
        </Vertical>
      </LineupWrapper>
      <DoubleAnimatedDiamond color={color} text={text} />
    </Root>
  );
};

ArenaStoryElement.fragments = {
  footballRivalsChallenge: gql`
    fragment ArenaStoryElement_footballRivalsChallenge on FootballRivalsChallenge {
      id
      rarityBonusEnabled
      homeContestant {
        id
        outcome
        score
        pointsDelta
        lineup {
          id
          ...Lineup_footballRivalsLineup
          ...TacticPreview_footballRivalsLineup
          ...ManagerPoints_footballRivalsLineup
        }
        ...Scores_footballRivalsContestant
      }
      awayContestant {
        id
        score
        pointsDelta
        lineup {
          id
          ...Lineup_footballRivalsLineup
          ...TacticPreview_footballRivalsLineup
          ...ManagerPoints_footballRivalsLineup
        }
        ...Scores_footballRivalsContestant
      }
    }
    ${Lineup.fragments.footballRivalsLineup}
    ${TacticPreview.fragments.footballRivalsLineup}
    ${ManagerPoints.fragments.footballRivalsLineup}
    ${Scores.fragments.footballRivalsContestant}
  ` as TypedDocumentNode<ArenaStoryElement_footballRivalsChallenge>,
};
